import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

export interface GscSnapshotQuery {
  query: string
  latestClicks: number
  latestImpressions: number
  latestCtr: number
  latestPosition: number
  previousPosition: number | null
  positionChange: number | null
  clicksChange: number | null
  dfsRank: number | null
  snapshotCount: number
}

export interface GscSnapshotTimeseries {
  snapshot_date: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

export interface GscTrendPoint {
  snapshot_date: string
  total_clicks: number
  total_impressions: number
  avg_position: number
}

/** List queries with latest metrics + week-over-week changes */
export function useGscSnapshotQueries(siteId: string) {
  return useQuery({
    queryKey: ['gsc-snapshots', 'queries', siteId],
    queryFn: () => apiClient.get<{
      queries: GscSnapshotQuery[]
      snapshotDate: string | null
      previousDate: string | null
    }>(`/api/sites/${siteId}/gsc/snapshots/queries`),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Time-series for a single query (for charts) */
export function useGscSnapshotTimeseries(siteId: string, query: string) {
  return useQuery({
    queryKey: ['gsc-snapshots', 'timeseries', siteId, query],
    queryFn: () => apiClient.get<{
      query: string
      timeseries: GscSnapshotTimeseries[]
    }>(`/api/sites/${siteId}/gsc/snapshots/query/${encodeURIComponent(query)}`),
    enabled: !!siteId && !!query,
    staleTime: 5 * 60 * 1000,
  })
}

/** Trigger immediate snapshot */
export function useTakeGscSnapshot(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<{ count: number }>(`/api/sites/${siteId}/gsc/snapshot`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gsc-snapshots', 'queries', siteId] })
    },
  })
}

/** Trigger 16-month backfill */
export function useGscBackfill(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<{ count: number }>(`/api/sites/${siteId}/gsc/backfill`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gsc-snapshots', 'queries', siteId] })
    },
  })
}

/** Enable weekly GSC snapshots */
export function useEnableGscSnapshots(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post(`/api/sites/${siteId}/gsc/snapshot/enable`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['site', siteId] })
    },
  })
}
